import { FC } from 'react';
import { Link } from 'react-router-dom';
import { HStack, Image, VStack } from '@chakra-ui/react';

import fadedFlower from 'assets/brand/faded-flower@2x.png';
import Button from 'components/_core/Button';
import Heading from 'components/_core/Heading';
import SunshineCard from 'components/_core/SunshineCard';
import Text from 'components/_core/Text';
import { StepProps } from 'components/Stepper/types';
import TrackView from 'features/Analytics/TrackView';
import * as routes from 'features/Bank/routes';
import { INTERCOM_LAUNCHER_CLASS_NAME } from 'features/Intercom';
import {
  BankTransferCreationStep,
  useBankTransferContext,
} from 'features/Payments/Pages/Transfers/contexts';
import * as Layout from 'layouts/StepperLayout';

import locales from './locales';

const Failure: FC<StepProps<BankTransferCreationStep>> = ({ goNext }) => {
  const { bankTransfer, setBankTransfer } = useBankTransferContext('failure');

  const isExternalTransfer = bankTransfer.transferType === 'external';
  const isInternationalTransfer = bankTransfer.transferType === 'international';

  const handleRetryClick = () => {
    if (isInternationalTransfer) {
      goNext({
        key: 'selectAmountAndCurrency',
        shouldReplaceCurrentStep: true,
      });
      return;
    }
    if (isExternalTransfer && bankTransfer.isInstant) {
      setBankTransfer((prevState) => ({
        ...prevState,
        isInstant: false,
      }));
      goNext({
        key: 'confirmExternalDetails',
        shouldReplaceCurrentStep: true,
      });
    } else if (isExternalTransfer) {
      goNext({
        key: 'setExternalDetails',
        shouldReplaceCurrentStep: true,
      });
    } else {
      goNext({
        key: 'setInternalDetails',
        shouldReplaceCurrentStep: true,
      });
    }
  };

  const isInstantTransfer = isExternalTransfer && bankTransfer.isInstant;

  return (
    <Layout.Root>
      <Layout.Body>
        <TrackView pageName="NewTransferFailure" />
        <SunshineCard.Group width="600px">
          <SunshineCard alignItems="center" textAlign="center">
            <VStack alignItems="center" padding="space-24" spacing="space-24">
              <Image
                alt=""
                boxSize="180px"
                role="presentation"
                src={fadedFlower}
              />
              <Heading size="lg">{locales.title}</Heading>
              <Text>
                {isInstantTransfer
                  ? locales.instantTransferDescription
                  : locales.regularTransferDescription}
              </Text>
            </VStack>
          </SunshineCard>
          <SunshineCard textAlign="center">
            <HStack justifyContent="space-between">
              {isInstantTransfer ? (
                <Button
                  as={Link}
                  margin="space-8"
                  to={routes.root}
                  variant="inline-primary"
                >
                  {locales.backToHomeButton}
                </Button>
              ) : (
                <Button
                  className={INTERCOM_LAUNCHER_CLASS_NAME}
                  margin="space-8"
                  variant="inline-primary"
                >
                  {locales.contactUsButton}
                </Button>
              )}

              <Button margin="space-8" onClick={handleRetryClick}>
                {isInstantTransfer
                  ? locales.retryWithRegularTransferButton
                  : locales.retryButton}
              </Button>
            </HStack>
          </SunshineCard>
        </SunshineCard.Group>
      </Layout.Body>
    </Layout.Root>
  );
};

export default Failure;
